import { useState } from 'react';

import Heading from '@zenbusiness/zui/Heading';
import LinearProgress from '@zenbusiness/zui/LinearProgress';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import Stack from '@zenbusiness/zui/Stack';
import PropTypes from 'prop-types';

import AddressLabel from '#client/components/AddressLabel';
import { FocusGrid, ContainerGrid, ActionTwoButtonsGrid } from '#client/components/Grid';
import { ADDRESS } from '#client/constants/application';
import { CARD_TYPE } from '#client/constants/cards';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFormStep from '#client/hooks/useFormStep';

import {
  BoxContentStyled,
  DialogContentStyled,
  FormControlLabelStyled,
  Wrapper
} from './CustomModal.styles';

import { CHOOSE_CARD, NO_VALUE_CARD, REASON_OF_REQUESTING, SUMMARY } from '../constants/card';

export default function SendingAddress({ loading, handleCreateNewCard, createCardLoading }) {
  const { setCurrentFormStep, data, setData } = useFormStep();
  const { selectedAddress, businessAddress, personalAddress } = data;
  const [addressTypeSelected, setAddressTypeSelected] = useState(selectedAddress);
  const nextButtonId = useEnterSubmit();
  const isEmptyPhysicalCard =
    data.cardId === NO_VALUE_CARD.PHYSICAL_CARD && data.cardType === CARD_TYPE.BUSINESS_DEBIT_CARD;

  const handleSubmit = () => {
    setData({
      ...data,
      selectedAddress: addressTypeSelected
    });

    // When user has not physical card, create it.
    if (isEmptyPhysicalCard) {
      handleCreateNewCard(addressTypeSelected);
      return;
    }

    // When user has physical card, go to summary screen and replace it.
    setCurrentFormStep(SUMMARY);
  };
  const handleBack = () => {
    // When user has not physical card, back to choosing card screen
    if (isEmptyPhysicalCard) {
      setCurrentFormStep(CHOOSE_CARD);
      return;
    }

    // When user has physical card, back to reason screen.
    setCurrentFormStep(REASON_OF_REQUESTING);
  };

  const handleChange = (e, value) => {
    setAddressTypeSelected(value);
  };

  return (
    <>
      <DialogContentStyled sx={{ padding: '0 8px' }}>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper spacing="32px">
              <Stack spacing="16px">
                <Heading
                  family="secondary"
                  size="3xl"
                  weight="bold"
                >
                  Where should we send it?
                </Heading>
              </Stack>
              {loading ? (
                <LinearProgress />
              ) : (
                <BoxContentStyled>
                  <RadioGroup
                    aria-label="Choose address where we should send the new card."
                    name="address-radio-buttons-group"
                    onChange={handleChange}
                    sx={{
                      gap: '32px'
                    }}
                    value={addressTypeSelected}
                  >
                    <FormControlLabelStyled
                      control={<Radio />}
                      label={
                        <AddressLabel
                          {...businessAddress}
                          type="Business Address"
                        />
                      }
                      value={ADDRESS.BUSINESS}
                    />
                    <FormControlLabelStyled
                      control={<Radio />}
                      label={
                        <AddressLabel
                          {...personalAddress}
                          type="Personal Address"
                        />
                      }
                      value={ADDRESS.PERSONAL}
                    />
                  </RadioGroup>
                </BoxContentStyled>
              )}
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionTwoButtonsGrid
        firstButton={
          <LoadingButton
            fullWidth
            onClick={handleBack}
            size="large"
            variant="outlined"
          >
            Back
          </LoadingButton>
        }
        secondButton={
          <LoadingButton
            color="primary"
            disabled={!addressTypeSelected}
            fullWidth
            id={nextButtonId}
            loading={createCardLoading}
            onClick={handleSubmit}
            size="large"
            variant="contained"
          >
            Next
          </LoadingButton>
        }
      />
    </>
  );
}

SendingAddress.propTypes = {
  loading: PropTypes.bool,
  handleCreateNewCard: PropTypes.func,
  createCardLoading: PropTypes.bool
};
